import { debug } from '../utils/debug';
import { warn } from '../utils/warn';

export const parseStreamedEvent = (
  event: string
):
  | {
      event:
        | 'messages'
        | 'messages/partial'
        | 'end'
        | 'metadata'
        | 'values'
        | 'heartbeat'
        | 'updates'
        | 'custom'; // Add 'custom' here
      data: unknown;
    }
  | undefined => {
  // console.log('🔧 parseStreamedEvent parsing input (length:', event.length, ')');

  // Add more detailed logging for debugging
  // console.log('🔧 Raw event first 500 chars:', event.substring(0, 500));
  // console.log('🔧 Raw event last 200 chars:', event.substring(Math.max(0, event.length - 200)));

  // Handle heartbeat events first
  if (event.trim().startsWith(': heartbeat') || event.trim() === ':') {
    // console.log('💓 Heartbeat event detected');
    return { event: 'heartbeat', data: null };
  }

  const lines = event.split(/\r?\n/);
  // console.log('🔧 Total lines in event:', lines.length);

  let eventName: string | undefined;
  let dataLines: string[] = [];

  // Log each line for debugging
  // lines.forEach((line, index) => {
  //   if (line.startsWith('event:') || line.startsWith('data:')) {
  //     console.log(`🔧 Line ${index}: ${line.substring(0, 100)}${line.length > 100 ? '...' : ''}`);
  //   }
  // });

  // Extract event name and data lines from current chunk
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    if (line.startsWith('event:')) {
      eventName = line.replace('event:', '').trim();
      // console.log('🔧 Found event name:', eventName);
    } else if (line.startsWith('data:')) {
      const dataStart = line.replace('data:', '').trim();
      dataLines.push(dataStart);

      for (let j = i + 1; j < lines.length; j++) {
        const nextLine = lines[j];
        if (nextLine.startsWith('id:') || nextLine.trim() === '') {
          break;
        }
        dataLines.push(nextLine);
        i = j;
      }
      break;
    }
  }

  // console.log('🔧 Event name:', eventName, 'Data lines count:', dataLines.length);

  if (!eventName) {
    console.warn('⚠️ No event name found in chunk');
    console.warn(
      '⚠️ Lines that start with "event:":',
      lines.filter((l) => l.startsWith('event:'))
    );
    console.warn(
      '⚠️ Lines that start with "data:":',
      lines.filter((l) => l.startsWith('data:')).length
    );

    // Check if this might be a custom event format
    if (event.includes('"custom"')) {
      // console.log('🔧 Detected "custom" in event - might be a custom event format');
    }

    return undefined;
  }

  // Check for custom events specifically
  if (eventName === 'custom') {
    // console.log('🎯 CUSTOM EVENT DETECTED');
    const rawData = dataLines.join('\n').trim();

    try {
      const data = JSON.parse(rawData);
      // console.log('🎯 Custom event data parsed:', JSON.stringify(data, null, 2));

      // Return as a 'custom' event directly
      return { event: 'custom', data }; // Changed from 'updates' to 'custom'
    } catch (error) {
      console.error('🎯 Failed to parse custom event data:', error);
      console.error('🎯 Raw custom data:', rawData);
      return undefined;
    }
  }

  if (
    eventName !== 'messages' &&
    eventName !== 'messages/partial' &&
    eventName !== 'updates' &&
    eventName !== 'end' &&
    eventName !== 'metadata' &&
    eventName !== 'values' &&
    eventName !== 'custom' // Add 'custom' to the validation
  ) {
    debug(`LangGraph stream adapter received unsupported event "${eventName}"`);
    // console.log(`🔧 Unsupported event type: "${eventName}"`);

    // Log the raw data for unsupported events to help debug
    const rawData = dataLines.join('\n').trim();
    // console.log(`🔧 Unsupported event raw data preview:`, rawData.substring(0, 500));

    return undefined;
  }

  const validEventName = eventName as
    | 'messages'
    | 'messages/partial'
    | 'updates'
    | 'end'
    | 'metadata'
    | 'values'
    | 'custom'; // Add 'custom' to the type assertion

  const rawData = dataLines.join('\n').trim();

  try {
    if (!rawData) {
      // console.log('✅ Empty data, returning undefined');
      return { event: validEventName, data: undefined };
    }

    // Parse the JSON (it's valid, just large)
    const data = JSON.parse(rawData);

    // Log summary instead of full data for large objects
    if (data && typeof data === 'object' && data.messages && Array.isArray(data.messages)) {
      // console.log(`✅ Parsed conversation with ${data.messages.length} messages`);
    } else if (data && typeof data === 'object') {
      // console.log('✅ parseStreamedEvent success, data keys:', Object.keys(data));

      // Check if this looks like a custom progress message
      if ('status' in data && 'message' in data) {
        // console.log('✅ Looks like a progress message:', data.status, '-', data.message);
      }
    } else {
      // console.log('✅ parseStreamedEvent success, data type:', typeof data);
    }

    return { event: validEventName, data };
  } catch (error) {
    console.warn('⚠️ Failed to parse JSON:', error);
    console.warn('⚠️ Raw data length:', rawData.length);
    console.warn('⚠️ Raw data preview (first 200):', rawData.substring(0, 200));
    console.warn(
      '⚠️ Raw data preview (last 200):',
      rawData.substring(Math.max(0, rawData.length - 200))
    );

    // Try to identify the structure of the unparseable data
    if (rawData.startsWith('{')) {
      // Find the position of the parse error
      try {
        // Try parsing progressively to find where it breaks
        for (let i = 1; i <= Math.min(rawData.length, 1000); i++) {
          JSON.parse(rawData.substring(0, i));
        }
      } catch (e) {
        console.warn('⚠️ JSON parse fails around character:', e.message);
      }
    }

    warn(`LangGraph stream adapter failed to parse data for event "${validEventName}"`);
    return { event: validEventName, data: undefined };
  }
};
